import { SelectionChange, SelectionModel } from '@angular/cdk/collections';
import { AsyncPipe, DecimalPipe, NgFor, NgIf } from '@angular/common';
import { Component, Injector, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatLegacyButtonModule } from '@angular/material/legacy-button';
import { MatLegacyCheckboxModule } from '@angular/material/legacy-checkbox';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { MatLegacyTooltipModule } from '@angular/material/legacy-tooltip';
import { MatTabsModule } from '@angular/material/tabs';
import _ from 'lodash';
import { Observable, Subject, catchError, tap, throwError } from 'rxjs';
import { CuttingCustomGrooveSchema } from '../../models/cutting/cutting-custom-groove-schema.model';
import { CuttingPanelBaseSlot, GrooveTypeEnum } from '../../models/cutting/cutting-panel-base-slot.model';
import { CuttingPanelSlot } from '../../models/cutting/cutting-panel-slot.model';
import { LegacyDialogsService } from '../../services/legacy-dialogs.service';
import {
  CustomSlotDetailDialogComponent,
  CustomSlotDetailDialogFormData,
  CustomSlotDetailDialogResponse,
} from './custom-slot-detail-dialog/custom-slot-detail-dialog.component';

interface CustomSchemasByType {
  inPlate: CuttingCustomGrooveSchema[];
  inSide: CuttingCustomGrooveSchema[];
  rabbet: CuttingCustomGrooveSchema[];
  chamfer: CuttingCustomGrooveSchema[];
  fillet: CuttingCustomGrooveSchema[];
  chamfer2: CuttingCustomGrooveSchema[];
}

@Component({
  selector: 'csl-slot-select',
  templateUrl: 'slot-select.component.html',
  styles: [
    `
      .simple {
        @apply border-b border-gray-300;

        .warn-col,
        .delete-col {
          @apply py-0 w-10;
        }
      }

      :host ::ng-deep .mat-mdc-tab {
        @apply h-[8.5rem] px-2 #{'!important'};

        .slot-img-wrapper {
          @apply w-52 h-32 p-2 rounded border border-gray-400;
          img {
            @apply h-full w-auto m-auto;
          }
        }
      }
    `,
  ],
  standalone: true,
  imports: [
    MatTabsModule,
    NgIf,
    NgFor,
    AsyncPipe,
    DecimalPipe,
    MatIconModule,
    MatLegacyButtonModule,
    MatLegacyCheckboxModule,
    MatLegacyTooltipModule,
  ],
})
export class SlotSelectComponent implements OnChanges {
  customFacadeSlots: CuttingCustomGrooveSchema[] = [];
  thisSlotTypeEnum = GrooveTypeEnum;
  private customSchemasByType: CustomSchemasByType;
  selection: SelectionModel<CuttingPanelBaseSlot> = new SelectionModel(false);
  save$: Observable<number>;
  facadeSlots$: Observable<CuttingCustomGrooveSchema[]>;
  action$: Observable<void>;

  @Input({ required: true }) slots: CuttingPanelBaseSlot[];
  @Input({ required: true }) postCustomSlotsData: (slot: CuttingPanelSlot, schemaId: number) => Observable<number>;
  @Input({ required: true }) deleteCustomSlot: (id: number) => Observable<void>;
  @Input() fullPanelThickness?: number;
  @Input() customSlotSchemas?: CuttingCustomGrooveSchema[];
  @Input() mapSchemaToSlot?: (formData: CustomSlotDetailDialogFormData<CuttingCustomGrooveSchema>) => CuttingPanelSlot;

  @Output() selectionChange: Subject<SelectionChange<CuttingPanelBaseSlot>> = this.selection.changed;

  constructor(public dialogsService: LegacyDialogsService, private injector: Injector, private snackBar: MatSnackBar) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['customSlotSchemas'] && changes['customSlotSchemas'].currentValue) {
      this.customFacadeSlots = changes['customSlotSchemas'].currentValue;

      this.customSchemasByType = {
        inPlate: this.customFacadeSlots.filter(s => s.type === GrooveTypeEnum.InPlate),
        inSide: this.customFacadeSlots.filter(s => s.type === GrooveTypeEnum.InSide),
        rabbet: this.customFacadeSlots.filter(s => s.type === GrooveTypeEnum.Rabbet),
        chamfer: this.customFacadeSlots.filter(s => s.type === GrooveTypeEnum.Chamfer),
        fillet: this.customFacadeSlots.filter(s => s.type === GrooveTypeEnum.Fillet),
        chamfer2: this.customFacadeSlots.filter(s => s.type === GrooveTypeEnum.Chamfer2),
      };
    }
  }

  hasCustomSlots(type: GrooveTypeEnum): boolean {
    switch (type) {
      case GrooveTypeEnum.InPlate:
        return !!this.customSchemasByType?.inPlate.length;
      case GrooveTypeEnum.InSide:
        return !!this.customSchemasByType?.inSide.length;
      case GrooveTypeEnum.Rabbet:
        return !!this.customSchemasByType?.rabbet.length;
      case GrooveTypeEnum.Chamfer:
        return !!this.customSchemasByType?.chamfer.length;
      case GrooveTypeEnum.Fillet:
        return !!this.customSchemasByType?.fillet.length;
      case GrooveTypeEnum.Chamfer2:
        return !!this.customSchemasByType?.chamfer2.length;
    }
  }

  hasSlots(type: GrooveTypeEnum): boolean {
    return this.slots.filter(g => g.type === type).length > 0 || this.hasCustomSlots(type);
  }

  openCustomSlotDetailDialog(slotType: GrooveTypeEnum): void {
    const slotSchemasByType = {
      [GrooveTypeEnum.InPlate]: this.customSchemasByType.inPlate,
      [GrooveTypeEnum.InSide]: this.customSchemasByType.inSide,
      [GrooveTypeEnum.Rabbet]: this.customSchemasByType.rabbet,
      [GrooveTypeEnum.Chamfer]: this.customSchemasByType.chamfer,
      [GrooveTypeEnum.Fillet]: this.customSchemasByType.fillet,
      [GrooveTypeEnum.Chamfer2]: this.customSchemasByType.chamfer2,
    };

    const customSlotSchemas = slotSchemasByType[slotType];

    const mapSchemaToSlot = this.mapSchemaToSlot ? this.mapSchemaToSlot : null;
    const dialogData = { grooveType: slotType, customSlotSchemas, mapSchemaToSlot };

    this.dialogsService
      .component(CustomSlotDetailDialogComponent, dialogData, 500)
      .subscribe((response: CustomSlotDetailDialogResponse | undefined) => {
        if (response) {
          this.save$ = this.postCustomSlotsData(response.groove, response.schemaId).pipe(
            tap(res => {
              response.groove.id = +res;
              response.groove.custom = true;
              this.slots.push(response.groove);

              this.snackBar.open($localize`Паз добавлен`);
            }),
            catchError(err => {
              this.snackBar.open($localize`Не удалось добавить паз`);
              return throwError(() => err);
            })
          );
        }
      });
  }

  deleteSlot(item: CuttingPanelBaseSlot): void {
    if (this.selection.isSelected(item)) {
      this.selection.deselect(item);
    }

    this.action$ = this.deleteCustomSlot(item.id).pipe(
      tap(() => {
        const slotIndex = this.slots.findIndex(g => g.id === item.id);

        if (slotIndex !== -1) {
          this.slots.splice(slotIndex, 1);
        }
      })
    );
  }

  compareDepthSlotWithFullPanelThickness(slot: CuttingPanelBaseSlot): boolean {
    if (!this.fullPanelThickness) {
      return false;
    }
    const totalSlotDepth =
      slot.type === GrooveTypeEnum.InSide
        ? slot.h + slot.dx
        : slot.type === GrooveTypeEnum.Fillet
        ? slot.r
        : slot.type === GrooveTypeEnum.Chamfer2
        ? slot.dx
        : slot.g;

    return totalSlotDepth > this.fullPanelThickness;
  }

  isShowWarnColumn(): boolean {
    let isShow = false;
    _.each(this.slots, s => {
      const totalSlotDepth =
        s.type === GrooveTypeEnum.InSide
          ? s.h + s.dx
          : s.type === GrooveTypeEnum.Fillet
          ? s.r
          : s.type === GrooveTypeEnum.Chamfer2
          ? s.dx
          : s.g;

      if (totalSlotDepth > this.fullPanelThickness) {
        isShow = true;
        return false;
      }
    });
    return isShow;
  }

  disabledAddYourSlotButton(slotType: GrooveTypeEnum): boolean {
    if (this.customSlotSchemas) {
      return !(this.customFacadeSlots.filter(s => s.type == slotType).length > 0);
    }
    return false;
  }
}
